import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter, StringCompare } from '@stamhoofd/utility';
import { Address } from '../addresses/Address.js';
import { STBillingStatus } from '../billing/STBillingStatus.js';
import { Organization } from '../Organization.js';
import { OrganizationEmail } from '../OrganizationEmail.js';
import { OrganizationPackages } from '../OrganizationMetaData.js';
import { AcquisitionType } from '../OrganizationPrivateMetaData.js';
import { OrganizationType } from '../OrganizationType.js';
import { UmbrellaOrganization } from '../UmbrellaOrganization.js';
import { User } from '../User.js';
export class OrganizationStats extends AutoEncoder {
}
__decorate([
    field({ decoder: IntegerDecoder })
], OrganizationStats.prototype, "activeWebshops", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], OrganizationStats.prototype, "memberCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], OrganizationStats.prototype, "orderCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], OrganizationStats.prototype, "webshopRevenue", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], OrganizationStats.prototype, "activeAdmins", void 0);
export class OrganizationPaymentMandateDetails extends AutoEncoder {
}
__decorate([
    field({ decoder: StringDecoder, optional: true })
], OrganizationPaymentMandateDetails.prototype, "consumerName", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], OrganizationPaymentMandateDetails.prototype, "consumerAccount", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], OrganizationPaymentMandateDetails.prototype, "consumerBic", void 0);
export class OrganizationPaymentMandate extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * The signature date of the mandate in YYYY-MM-DD format.
         */
        this.signatureDate = null;
        this.mandateReference = null;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], OrganizationPaymentMandate.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OrganizationPaymentMandate.prototype, "method", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OrganizationPaymentMandate.prototype, "status", void 0);
__decorate([
    field({ decoder: OrganizationPaymentMandateDetails })
], OrganizationPaymentMandate.prototype, "details", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], OrganizationPaymentMandate.prototype, "signatureDate", void 0);
__decorate([
    field({ decoder: DateDecoder })
], OrganizationPaymentMandate.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], OrganizationPaymentMandate.prototype, "mandateReference", void 0);
export class OrganizationSummary extends Organization {
    constructor() {
        super(...arguments);
        this.lastActiveAt = null;
        this.acquisitionTypes = [];
        this.mollieCustomerId = null;
        this.paymentMandates = [];
        this.features = [];
    }
}
__decorate([
    field({ decoder: STBillingStatus })
], OrganizationSummary.prototype, "billingStatus", void 0);
__decorate([
    field({ decoder: DateDecoder })
], OrganizationSummary.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], OrganizationSummary.prototype, "lastActiveAt", void 0);
__decorate([
    field({ decoder: OrganizationStats })
], OrganizationSummary.prototype, "stats", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(AcquisitionType)), version: 130 })
], OrganizationSummary.prototype, "acquisitionTypes", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 154 })
], OrganizationSummary.prototype, "mollieCustomerId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationPaymentMandate), version: 154 })
], OrganizationSummary.prototype, "paymentMandates", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), optional: true })
], OrganizationSummary.prototype, "features", void 0);
export class OrganizationOverview extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.umbrellaOrganization = null;
        this.emails = [];
        this.features = [];
        this.acquisitionTypes = [];
    }
    matchQuery(q) {
        if (q === this.id) {
            return true;
        }
        const parts = q.split(/[ -]/);
        const name = Formatter.slug(this.name);
        const orgParts = name.split(/[ -]/);
        if (q.includes('@')) {
            if (this.emails.some(e => e.email.toLocaleLowerCase() == q.toLocaleLowerCase())) {
                return true;
            }
            if (this.admins.some(a => a.email.toLocaleLowerCase() == q.toLocaleLowerCase())) {
                return true;
            }
        }
        for (const [index, part] of parts.entries()) {
            if (part.length > 1 || index >= parts.length - 1) {
                if (index < parts.length - 1) {
                    // Should be a full match of a word in name
                    if (!orgParts.some(o => o.toLocaleLowerCase() == (part.toLocaleLowerCase()))) {
                        if (StringCompare.typoCount(this.address.city, part) > 0) {
                            return false;
                        }
                    }
                    continue;
                }
                if (name.toLocaleLowerCase().includes(part.toLocaleLowerCase())) {
                    return true;
                }
                if (StringCompare.typoCount(this.address.city, part) == 0) {
                    return true;
                }
            }
        }
        if (StringCompare.typoCount(name, q) < 2) {
            return true;
        }
        if (StringCompare.typoCount(this.address.city, q) < 2) {
            return true;
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], OrganizationOverview.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OrganizationOverview.prototype, "name", void 0);
__decorate([
    field({ decoder: new EnumDecoder(OrganizationType), version: 90 })
], OrganizationOverview.prototype, "type", void 0);
__decorate([
    field({ decoder: new EnumDecoder(UmbrellaOrganization), nullable: true, version: 90 })
], OrganizationOverview.prototype, "umbrellaOrganization", void 0);
__decorate([
    field({ decoder: Address })
], OrganizationOverview.prototype, "address", void 0);
__decorate([
    field({ decoder: DateDecoder })
], OrganizationOverview.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: OrganizationPackages })
], OrganizationOverview.prototype, "packages", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(User), optional: true, version: 90 })
], OrganizationOverview.prototype, "admins", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationEmail), version: 90 })
], OrganizationOverview.prototype, "emails", void 0);
__decorate([
    field({ decoder: OrganizationStats, version: 90 })
], OrganizationOverview.prototype, "stats", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), optional: true })
], OrganizationOverview.prototype, "features", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(AcquisitionType)), optional: true })
], OrganizationOverview.prototype, "acquisitionTypes", void 0);
//# sourceMappingURL=OrganizationSummary.js.map